import { useEffect, useRef } from 'react';

/**
 * @typedef {Object} UseInfiniteScrollOptions
 * @property {() => void} onReachEnd - Callback fired when the sentinel element becomes visible.
 * @property {boolean} [disabled=false] - When true the observer is disconnected and onReachEnd won't fire.
 */

/**
 * @typedef {Object} UseInfiniteScrollReturn
 * @property {React.MutableRefObject<HTMLElement|null>} ref - Ref to attach to the sentinel (last list item).
 */

/**
 * Detects when the user scrolls to the end of a list via IntersectionObserver.
 * Attach the returned `ref` to the last visible element in the scrollable container.
 *
 * @param {UseInfiniteScrollOptions} options
 * @returns {UseInfiniteScrollReturn}
 */
function useInfiniteScroll({ onReachEnd, disabled = false }) {
  const ref = useRef(null);

  useEffect(() => {
    if (disabled || !ref.current) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        onReachEnd();
        observer.unobserve(entries[0].target);
      }
    });

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [onReachEnd, disabled]);

  return { ref };
}

export default useInfiniteScroll;
